import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { View, ScrollView, StyleSheet } from "react-native";
import { useAppState } from "../../../contexts/AppStateContext";
import MessageContainer from "../MessageContainer/MessageContainer";
import CompanionMessagesContainer, { type MessageItem } from "../../companion/MessagesContainer";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import { DownArrow } from "../../../components/svgs/common";
import { useChatBar } from "../../../contexts/ChatBarContext";
import ProcessingMessageBox from "../../../components/chat/LoadingMessage/ProcessingMessageBox";
import { useFocusEffect } from "@react-navigation/native";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";

const CONTAINER_PADDING_BOTTOM = 16;

type MessageOverride = { message?: string; author?: string };

interface ChatContainerProps {
  chatData: { conversation?: Array<{ content?: string; sender?: string; id?: string }> } | undefined;
  contentOverride?: MessageOverride;
  currentMessageOverride?: MessageOverride;
  optimisticHumanMessage?: string | null;
  resetAutoScrollTrigger?: number;
  useCompanionMessagesContainer?: boolean;
}

function ChatContainer({
  chatData,
  contentOverride,
  currentMessageOverride,
  optimisticHumanMessage = null,
  resetAutoScrollTrigger = 0,
  useCompanionMessagesContainer = false,
}: ChatContainerProps) {
  const { state } = useAppState();
  const scrollViewRef = useRef(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [downButton, setDownButton] = useState(false);
  const [humanMessageOffset, setHumanMessageOffset] = useState(0);
  const [scrollViewHeight, setScrollViewHeight] = useState(undefined);
  const [parentHeight, setParentHeight] = useState(0);

  const [isUserInteracted, setIsUserInteracted] = useState(false);

  const isAtBottomRef = useRef(true);
  const { state: chatBarState } = useChatBar();

  useFocusEffect(
    useCallback(() => {
      return () => {
        setIsUserInteracted(false);
      };
    }, [])
  );

  const handleScroll = useCallback((event) => {
    const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
    const isAtBottom = layoutMeasurement.height + contentOffset.y >= contentSize.height - 50;

    if (!isAtBottom) {
      setIsUserScrolling(true);
      setDownButton(true);
    } else {
      setIsUserScrolling(false);
      setDownButton(false);
    }

    isAtBottomRef.current = isAtBottom;
  }, []);

  const scrollToBottom = (animated = false) => {
    if (scrollViewRef.current) {
      scrollViewRef.current.scrollToEnd({ animated });
    }
  };

  const scrollToBottomButton = () => {
    setDownButton(false);
    scrollToBottom(true);
  };

  useEffect(() => {
    if (scrollViewRef.current) {
      scrollViewRef.current.setAttribute("data-sensitive", "true");
    }
  }, []);

  useEffect(() => {
    if (isUserInteracted && humanMessageOffset > 0 && !isUserScrolling) {
      scrollViewRef.current.scrollTo({ y: humanMessageOffset });
    } else {
      scrollToBottom();
    }
  }, [humanMessageOffset]);

  const effectiveContent = contentOverride ?? (state.content as MessageOverride);
  const effectiveCurrentMessage =
    currentMessageOverride ?? (state.currentMessage?.content as MessageOverride | undefined);

  const contentAuthor = useMemo(
    () => (typeof effectiveContent?.author === "string" ? effectiveContent.author.toLowerCase() : ""),
    [effectiveContent?.author]
  );
  const contentMessage = useMemo(
    () => (typeof effectiveContent?.message === "string" ? effectiveContent.message : ""),
    [effectiveContent?.message]
  );

  useEffect(() => {
    if (contentAuthor === "human") {
      setIsUserInteracted(true);
    } else if (!isUserInteracted) {
      scrollToBottom();
    }
  }, [state.content?.author]);

  const [sortedConversation, setSortedConversation] = useState([]);
  const [resetAutoScroll, setResetAutoScroll] = useState(0);

  const streamingMessage = useMemo(() => {
    const currentContent = effectiveCurrentMessage;
    if (!currentContent?.message || currentContent.author?.toLowerCase() !== "ai") {
      return null;
    }
    return {
      id: "streaming-message",
      sender: "ai",
      content: currentContent.message,
    };
  }, [effectiveCurrentMessage]);

  const conversationWithOptimisticHuman = useMemo(() => {
    const message = optimisticHumanMessage?.trim() ?? "";
    if (!message) {
      return sortedConversation;
    }

    const hasSameFinalHumanMessage = sortedConversation.some(
      (item) => item.sender === "human" && item.content?.trim() === message
    );

    if (hasSameFinalHumanMessage) {
      return sortedConversation;
    }

    return [...sortedConversation, { id: "optimistic-human-message", sender: "human", content: message }];
  }, [sortedConversation, optimisticHumanMessage]);

  const displayedConversation = useMemo(() => {
    if (!streamingMessage) {
      return conversationWithOptimisticHuman;
    }
    const lastMessage = conversationWithOptimisticHuman[conversationWithOptimisticHuman.length - 1];
    if (lastMessage?.sender === "ai" && lastMessage?.content === streamingMessage.content) {
      return conversationWithOptimisticHuman;
    }
    return [...conversationWithOptimisticHuman, streamingMessage];
  }, [conversationWithOptimisticHuman, streamingMessage]);

  useEffect(() => {
    if (chatData?.conversation) {
      setSortedConversation([...chatData.conversation].reverse());
    } else {
      setSortedConversation([]);
    }
  }, [chatData]);

  useEffect(() => {
    if (!contentMessage) return;
    if (contentAuthor !== "ai") return;

    const conversation = chatData?.conversation ? [...chatData.conversation].reverse() : [];

    const newAIMessage = {
      content: contentMessage,
      sender: contentAuthor,
    };

    const isNew = !conversation.some((item) => item.content === newAIMessage.content);
    const combinedChatData = isNew ? [...conversation, newAIMessage] : conversation;

    setSortedConversation(combinedChatData);
  }, [contentAuthor, contentMessage, chatData]);

  useEffect(() => {
    if (contentAuthor === "human" && contentMessage.trim()) {
      setResetAutoScroll((prev) => prev + 1);
    }
  }, [contentAuthor, contentMessage]);

  const companionMessages = useMemo<MessageItem[]>(
    () =>
      displayedConversation.map((item, index) => ({
        id: item.id ?? `message-${index}`,
        sender: item.sender ?? "ai",
        content: item.content ?? "",
        todos: "todos" in item ? item.todos : undefined,
        assignments: "assignments" in item ? item.assignments : undefined,
        files: "files" in item ? item.files : undefined,
      })),
    [displayedConversation]
  );

  if (useCompanionMessagesContainer) {
    return (
      <CompanionMessagesContainer
        messages={companionMessages}
        isStreaming={chatBarState.llmMessage.meta.isLoading}
        currentStreamingMessageId={streamingMessage?.id ?? null}
        resetAutoScroll={resetAutoScroll + resetAutoScrollTrigger}
      />
    );
  }

  const handleOnLayout = (event, item, array) => {
    const hasNoHumanMessageYet = array.every((item) => item.sender !== "human");
    const isLastAiMessage = array.findLast((item) => item.sender === "ai")?.id === item.id;

    if (hasNoHumanMessageYet && isLastAiMessage) {
      const lastAiMessageOffsetTop = event.nativeEvent.layout.y;

      setScrollViewHeight(lastAiMessageOffsetTop + parentHeight - CONTAINER_PADDING_BOTTOM);
    } else {
      const isLastHumanMessage = array.findLast((item) => item.sender === "human")?.id === item.id;

      if (isLastHumanMessage && isUserInteracted) {
        const humanMessageOffsetFromTop = event.nativeEvent.layout.y;

        setHumanMessageOffset(humanMessageOffsetFromTop);
        setScrollViewHeight(humanMessageOffsetFromTop + parentHeight - CONTAINER_PADDING_BOTTOM);
      }
    }
  };

  return (
    <View
      style={[styles.container, { paddingBottom: CONTAINER_PADDING_BOTTOM }]}
      onLayout={(event) => setParentHeight(event.nativeEvent.layout.height)}>
      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollView}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        maintainVisibleContentPosition={{
          minIndexForVisible: 0,
          autoscrollToTopThreshold: 10,
        }}
        showsVerticalScrollIndicator={true}
        contentContainerStyle={{ height: scrollViewHeight }}
        onContentSizeChange={() => {
          if (!isUserInteracted) {
            scrollToBottom();
          }
        }}
        tabIndex={0}>
        <View style={styles.view}>
          {displayedConversation.map((item, index, array) => (
            <View
              onLayout={(event) => {
                handleOnLayout(event, item, array);
              }}
              key={index}>
              <MessageContainer message={item} index={index} />
            </View>
          ))}
          {chatBarState.llmMessage.meta.isLoading && (
            <View>
              <CustomSpacing type="vertical" size="s" />
              <ProcessingMessageBox />
            </View>
          )}
        </View>
      </ScrollView>
      {downButton && (
        <View style={styles.buttonContainer}>
          <CustomButton
            styleType="primary"
            style={styles.button}
            leftIcon={<DownArrow />}
            onPress={scrollToBottomButton}
            rightIcon={undefined}
            text={undefined}
            textProps={undefined}
            textStyle={undefined}
            disabled={false}
            accessibilityLabel="Scroll to bottom"
          />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    zIndex: 800,
  },
  scrollView: {
    flex: 1,
    width: "100%",
    paddingHorizontal: 0,
    zIndex: 0,
  },
  view: {
    width: "100%",
    alignSelf: "center",
    maxWidth: 704,
    paddingTop: 30,
    paddingHorizontal: 16,
    paddingBottom: 100,
  },
  button: {
    justifyContent: "center",
    position: "absolute",
    alignItems: "center",
    bottom: 10,
    width: 30,
    height: 30,
    borderRadius: 25,
    zIndex: 5,
  },
  buttonContainer: {
    alignItems: "center",
  },
});

export default ChatContainer;
